import { useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { LineChart } from '@mui/x-charts/LineChart';
import { api } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { formatCents, formatTimestamp } from '../lib/format';
import { monthLabel } from '../lib/period';
import { PageHelp } from '../components/PageHelp';

// Eine Reihe, Farbe aus der validierten Palette (wie Einnahmen im Dashboard).
const TOTAL_COLOR = '#2a78d6';

interface Account {
  accountId: string;
  name: string;
  type: string;
  balanceCents: number;
  monthEnds: Record<string, number>;
  show: boolean;
}

interface LiquidityResponse {
  asOf: string | null;
  accounts: Account[];
}

type Range = '12' | '24' | 'alle';

const euro = (cents: number) => formatCents(cents);
const num = { align: 'right' as const, sx: { whiteSpace: 'nowrap' as const } };
const TYPE_LABEL: Record<string, string> = {
  online: 'Bank',
  register: 'Kasse',
  offline: 'Offline',
};

/**
 * Liquidität: Kontostände der gewählten Zahlungskonten (Girokonto, Kasse) aus SevDesk -- heute
 * und zum Monatsende. Nur Admin und Vorstand (Vereinszahlen ohne Abteilungsbezug).
 */
export default function LiquiditaetPage() {
  const { hasRole } = useAuth();
  const isAdmin = hasRole('admin');
  const [data, setData] = useState<LiquidityResponse>();
  const [error, setError] = useState<string>();
  const [range, setRange] = useState<Range>('24');
  const [asTable, setAsTable] = useState(false);

  async function load() {
    setData(await api.get<LiquidityResponse>('/admin/liquidity'));
  }

  useEffect(() => {
    load().catch((err: unknown) =>
      setError(err instanceof Error ? err.message : 'Kontostände konnten nicht geladen werden'),
    );
  }, []);

  async function toggle(account: Account) {
    setError(undefined);
    try {
      await api.put(`/admin/liquidity/accounts/${encodeURIComponent(account.accountId)}`, {
        show: !account.show,
      });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Änderung fehlgeschlagen');
    }
  }

  const shown = useMemo(() => (data?.accounts ?? []).filter((a) => a.show), [data]);
  // Monatsenden aller gezeigten Konten, älteste zuerst, dazu "heute" als letzter Punkt.
  const rows = useMemo(() => {
    const months = [...new Set(shown.flatMap((a) => Object.keys(a.monthEnds)))].sort();
    const visible = range === 'alle' ? months : months.slice(-Number(range));
    const all = visible.map((m) => ({
      key: m,
      label: monthLabel(m),
      values: shown.map((a) => a.monthEnds[m] ?? 0),
    }));
    if (shown.length)
      all.push({ key: 'heute', label: 'heute', values: shown.map((a) => a.balanceCents) });
    return all.map((r) => ({ ...r, total: r.values.reduce((s, v) => s + v, 0) }));
  }, [shown, range]);
  const totalToday = shown.reduce((s, a) => s + a.balanceCents, 0);

  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Typography variant="h4">Liquidität</Typography>
        <PageHelp text="Kontostände aus SevDesk: Stand heute und zum Ende jedes Monats. SevDesk rechnet den Stand aus allen erfassten Umsätzen; er stimmt mit dem Kontoauszug überein, solange in SevDesk alle Umsätze abgeglichen sind. Aktualisiert mit jedem stündlichen Import." />
      </Stack>
      {data && (
        <Typography variant="body2" color="text.secondary">
          Stand: {data.asOf ? formatTimestamp(data.asOf) : 'noch kein Import'}
        </Typography>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      {!data && !error && (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      )}
      {data && shown.length === 0 && (
        <Alert severity="info">
          Noch keine Kontostände{isAdmin ? ' – unten Konten auswählen.' : '.'}
        </Alert>
      )}

      {shown.length > 0 && (
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          {shown.map((a) => (
            <Paper key={a.accountId} sx={{ p: 2, flex: 1, minWidth: 180 }}>
              <Typography variant="body2" color="text.secondary">
                {a.name}
              </Typography>
              <Typography variant="h5" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {euro(a.balanceCents)}
              </Typography>
            </Paper>
          ))}
          {shown.length > 1 && (
            <Paper sx={{ p: 2, flex: 1, minWidth: 180 }}>
              <Typography variant="body2" color="text.secondary">
                Liquidität gesamt
              </Typography>
              <Typography variant="h5" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                <strong>{euro(totalToday)}</strong>
              </Typography>
            </Paper>
          )}
        </Stack>
      )}

      {rows.length > 1 && (
        <Paper sx={{ p: 2 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{ justifyContent: 'space-between', alignItems: { sm: 'center' } }}
          >
            <Typography variant="h6">Verlauf zum Monatsende</Typography>
            <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
              <TextField
                select
                size="small"
                label="Zeitraum"
                value={range}
                onChange={(e) => setRange(e.target.value as Range)}
                sx={{ minWidth: 150 }}
              >
                <MenuItem value="12">12 Monate</MenuItem>
                <MenuItem value="24">24 Monate</MenuItem>
                <MenuItem value="alle">Alle seit 2018</MenuItem>
              </TextField>
              <FormControlLabel
                control={
                  <Switch checked={asTable} onChange={(e) => setAsTable(e.target.checked)} />
                }
                label="Als Tabelle"
              />
            </Stack>
          </Stack>
          {asTable ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Monatsende</TableCell>
                    {shown.map((a) => (
                      <TableCell key={a.accountId} {...num}>
                        {a.name}
                      </TableCell>
                    ))}
                    {shown.length > 1 && <TableCell {...num}>Gesamt</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {[...rows].reverse().map((r) => (
                    <TableRow key={r.key}>
                      <TableCell>{r.label}</TableCell>
                      {r.values.map((v, i) => (
                        <TableCell key={i} {...num}>
                          {euro(v)}
                        </TableCell>
                      ))}
                      {shown.length > 1 && (
                        <TableCell {...num}>
                          <strong>{euro(r.total)}</strong>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Box sx={{ minWidth: Math.max(360, rows.length * 28) }}>
                <LineChart
                  dataset={rows.map((r) => ({ label: r.label, total: r.total / 100 }))}
                  height={300}
                  grid={{ horizontal: true }}
                  xAxis={[{ scaleType: 'point', dataKey: 'label' }]}
                  yAxis={[
                    {
                      valueFormatter: (v: number | null) => `${(v ?? 0).toLocaleString('de-DE')} €`,
                    },
                  ]}
                  series={[
                    {
                      dataKey: 'total',
                      label: shown.length > 1 ? 'Liquidität gesamt' : shown[0]!.name,
                      color: TOTAL_COLOR,
                      showMark: rows.length <= 36,
                      valueFormatter: (v: number | null) => euro(Math.round((v ?? 0) * 100)),
                    },
                  ]}
                  hideLegend
                />
              </Box>
            </Box>
          )}
        </Paper>
      )}

      {isAdmin && data && data.accounts.length > 0 && (
        <TableContainer component={Paper}>
          <Typography variant="h6" sx={{ p: 2, pb: 0 }}>
            Konten
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
            Welche Zahlungskonten aus SevDesk in der Liquidität erscheinen.
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Konto</TableCell>
                <TableCell>Art</TableCell>
                <TableCell {...num}>Stand heute</TableCell>
                <TableCell>Anzeigen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.accounts.map((a) => (
                <TableRow key={a.accountId}>
                  <TableCell>{a.name}</TableCell>
                  <TableCell>{TYPE_LABEL[a.type] ?? a.type}</TableCell>
                  <TableCell {...num}>{euro(a.balanceCents)}</TableCell>
                  <TableCell>
                    <Switch
                      checked={a.show}
                      onChange={() => void toggle(a)}
                      slotProps={{ input: { 'aria-label': `${a.name} anzeigen` } }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Stack>
  );
}
